'use client';

import { useLocale } from 'next-intl';
import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import { useSellToCountry } from '@/queries/Countries';
import type { Countries_content_countries } from '@/queries/types/Countries';

export interface GeoProviderProps {
  readonly countryCode: string;
}

interface GeoContext {
  countryData:
    | Countries_content_countries
    | { countryCode: string; currency: string; eu: boolean }
    | undefined;
  locale: string;
  isGeoReady: boolean;
}

const Context = createContext<GeoContext | null>(null);

export const useGeoContext = () => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('Did you forget to include GeoProvider in your tree?');
  }
  return ctx;
};

const GeoProvider = ({
  children,
  countryCode,
}: PropsWithChildren<GeoProviderProps>) => {
  const locale = useLocale();
  const { data, isSuccess, isLoading } = useSellToCountry(countryCode);
  const {
    data: usData,
    isSuccess: isUSSuccess,
    isLoading: isUSLoading,
  } = useSellToCountry('US');

  const countries = data?.countries;
  const usCountries = usData?.countries;

  const countryData =
    // eslint-disable-next-line no-nested-ternary
    isSuccess && countries
      ? countries[0]
      : isUSSuccess && usCountries
        ? usCountries[0]
        : { countryCode: 'US', currency: 'USD', eu: false };

  const isGeoReady = !isLoading && !isUSLoading;

  return (
    <Context.Provider value={{ countryData: countryData!, locale, isGeoReady }}>
      {children}
    </Context.Provider>
  );
};

export default GeoProvider;
